import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartHandshake, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function PaywallModal() {
  const { paywall, closePaywall } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    if (paywall.open && plans.length === 0) {
      api.get("/billing/plans").then(({ data }) => setPlans(data.plans)).catch(() => {});
    }
  }, [paywall.open, plans.length]);

  if (!paywall.open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-[#1F2A2E]/50" onClick={closePaywall} />
      <div
        data-testid="paywall-modal"
        className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl border border-[#E3DED4] shadow-2xl p-6 sm-fade-up"
      >
        <button
          onClick={closePaywall}
          data-testid="paywall-close"
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-[#F2EFE8]"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="w-12 h-12 rounded-full bg-[#D7EEF0] flex items-center justify-center">
          <HeartHandshake className="w-6 h-6 text-[#1F6F78]" />
        </div>
        <h2 className="font-display text-2xl font-bold text-[#1F2A2E] mt-4">
          Keep your recovery connections going
        </h2>
        <p data-testid="paywall-message" className="mt-2 text-sm text-[#4B5A60] leading-6">
          {paywall.message ||
            "You've used your free messages. Choose an affordable membership to continue messaging your recovery connections."}
        </p>

        <div className="mt-5 grid gap-2">
          {plans.map((p) => (
            <div
              key={p.lookup_key}
              className={`flex items-center justify-between rounded-xl px-4 py-3 bg-[#F7F4EF] ${
                p.badge === "Recommended" ? "border-2 border-[#1F6F78]" : "border border-[#E3DED4]"
              }`}
            >
              <div>
                <p className="text-sm font-bold text-[#1F2A2E]">
                  {p.label}{" "}
                  {p.badge ? (
                    <span className="ml-1 text-[10px] font-bold uppercase text-[#1F6F78]">
                      {p.badge}
                    </span>
                  ) : null}
                </p>
                <p className="text-xs text-[#4B5A60]">Unlimited messaging, cancel anytime</p>
              </div>
              <span className="font-display font-bold text-[#1F2A2E]">
                {p.price_display}
                <span className="text-xs font-normal text-[#4B5A60]">{p.period_display}</span>
              </span>
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs text-[#4B5A60]">
          Browsing sponsors, blocking, reporting and emergency resources always stay free.
        </p>

        <div className="mt-5 flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => {
              closePaywall();
              navigate("/pricing");
            }}
            data-testid="paywall-view-plans"
            className="flex-1 bg-[#1F6F78] hover:bg-[#185B62] text-white"
          >
            View Plans
          </Button>
          <Button
            variant="outline"
            onClick={closePaywall}
            data-testid="paywall-maybe-later"
            className="flex-1 bg-white border-[#E3DED4] text-[#1F2A2E] hover:bg-[#F2EFE8]"
          >
            Maybe Later
          </Button>
        </div>
        <button
          onClick={() => {
            closePaywall();
            navigate("/pricing#access-pass");
          }}
          data-testid="paywall-free-pass-link"
          className="mt-3 w-full text-center text-sm font-semibold text-[#1F6F78] underline"
        >
          Need access but can&apos;t afford it?
        </button>
      </div>
    </div>
  );
}
